import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { supabase } from '../../utils/supabase';
import { Crop, CropState } from '../../types/crops';

export const fetchCrops = createAsyncThunk(
  'crops/fetchCrops',
  async (_, thunkAPI) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      const { data, error } = await supabase
        .from('crops')
        .select(`
          *,
          variety:variety_id (
            id,
            name,
            description
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Crop[];
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const addCrop = createAsyncThunk(
  'crops/addCrop',
  async (crop: Omit<Crop, 'id' | 'created_at' | 'user_id'>, thunkAPI) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      const { data, error } = await supabase
        .from('crops')
        .insert([{ ...crop, user_id: user.id }])
        .select(`
          *,
          variety:variety_id (
            id,
            name,
            description
          )
        `)
        .single();

      if (error) throw error;
      return data as Crop;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const updateCrop = createAsyncThunk(
  'crops/updateCrop',
  async ({ id, ...updates }: Partial<Crop> & { id: string }, thunkAPI) => {
    try {
      const { data, error } = await supabase
        .from('crops')
        .update(updates)
        .eq('id', id)
        .select(`
          *,
          variety:variety_id (
            id,
            name,
            description
          )
        `)
        .single();

      if (error) throw error;
      return data as Crop;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const deleteCrop = createAsyncThunk(
  'crops/deleteCrop',
  async (id: string, thunkAPI) => {
    try {
      // First check if there are any associated sales
      const { data: sales, error: salesError } = await supabase
        .from('sales')
        .select('id, quantity, unit_price, sale_date')
        .eq('crop_id', id);

      if (salesError) throw salesError;

      if (sales && sales.length > 0) {
        return thunkAPI.rejectWithValue({
          type: 'CROP_HAS_SALES',
          message: 'Cannot delete crop with associated sales',
          salesCount: sales.length,
          sales: sales
        });
      }

      // If no sales, proceed with deletion
      const { error } = await supabase
        .from('crops')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return id;
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        type: 'DELETE_ERROR',
        message: error.message
      });
    }
  }
);

const initialState: CropState = {
  crops: [],
  loading: false,
  error: null,
};

const cropSlice = createSlice({
  name: 'crops',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCrops.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCrops.fulfilled, (state, action) => {
        state.loading = false;
        state.crops = action.payload;
        state.error = null;
      })
      .addCase(fetchCrops.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(addCrop.fulfilled, (state, action) => {
        state.crops.unshift(action.payload);
      })
      .addCase(updateCrop.fulfilled, (state, action) => {
        const index = state.crops.findIndex(c => c.id === action.payload.id);
        if (index !== -1) {
          state.crops[index] = action.payload;
        }
      })
      .addCase(deleteCrop.fulfilled, (state, action) => {
        state.crops = state.crops.filter(c => c.id !== action.payload);
      })
      .addCase(deleteCrop.rejected, (state, action) => {
        state.error = typeof action.payload === 'string' 
          ? action.payload 
          : (action.payload as any)?.message || 'Failed to delete crop';
      });
  },
});

export const { clearError } = cropSlice.actions;
export const cropReducer = cropSlice.reducer;
export default cropSlice.reducer;